import React, { useRef, useState } from 'react';
import MutateGate from '../Common/MutateGate';
import PriceListPosConfigPreviewModal from './PriceListPosConfigPreviewModal';
import {
  downloadPosConfigSheet,
  fetchAllPosConfigProducts,
  analyzePosConfigImport,
  executePosConfigImport,
  summarizePosConfigResult,
} from '../../utils/ventasPosConfigExcel';

const btnClass =
  'px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const PriceListPosConfigExcelToolbar = ({ activePosId, activePos, onUpdated, onToast }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const posLabel = activePos ? `${activePos.code || ''} ${activePos.name || ''}`.trim() : '';

  const closePreview = () => {
    if (confirming) return;
    setPreviewOpen(false);
    setPreviewState(null);
  };

  const handleDownload = async () => {
    if (!activePosId) return;
    setBusy(true);
    try {
      const products = await fetchAllPosConfigProducts(activePosId);
      await downloadPosConfigSheet(products, posLabel);
      onToast?.(
        `Planilla descargada con ${products.length} producto(s) del catálogo vendible.`,
        'success',
      );
    } catch (err) {
      const details = err.response?.data?.errors;
      const extra = details ? Object.values(details).flat().join(' ') : '';
      const message = err.response?.data?.message || err?.message || 'No se pudo generar la planilla Excel.';
      onToast?.(extra ? `${message} ${extra}` : message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activePosId) return;

    setBusy(true);
    try {
      const analysis = await analyzePosConfigImport(file, {
        pointOfSaleId: activePosId,
      });
      if (!analysis.success) {
        onToast?.(analysis.fatalMessage || 'No se pudo leer el archivo.', 'error');
        return;
      }
      setPreviewState(analysis);
      setPreviewOpen(true);
    } catch (err) {
      onToast?.(
        err?.message || 'No se pudo leer el archivo. Use la planilla .xlsx descargada.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewState?.success || !activePosId) return;

    setConfirming(true);
    try {
      const exec = await executePosConfigImport(previewState.validTasks, activePosId);
      const message = summarizePosConfigResult(exec);
      const hasErr = exec.errors?.length > 0;
      const type = exec.updated > 0 && !hasErr ? 'success' : hasErr ? 'warning' : 'info';

      onToast?.(message, type);
      setPreviewOpen(false);
      setPreviewState(null);
      if (exec.updated > 0) {
        onUpdated?.();
      }
    } catch (err) {
      onToast?.(err?.message || 'Error al aplicar la configuración.', 'error');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <p className="text-sm text-gray-600 flex-1 min-w-[12rem]">
          Solo productos marcados como vendibles en Productos. Indique con el check o el Excel cuáles venden en
          este POS.
        </p>
        <MutateGate>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy || confirming || !activePosId}
              className={btnClass}
            >
              {busy ? 'Procesando…' : 'Descargar Excel'}
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy || confirming || !activePosId}
              className="px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
            >
              Subir planilla editada
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={onFileChange}
            />
          </div>
        </MutateGate>
      </div>

      <PriceListPosConfigPreviewModal
        isOpen={previewOpen}
        onClose={closePreview}
        preview={previewState}
        onConfirm={handleConfirm}
        confirming={confirming}
      />
    </>
  );
};

export default PriceListPosConfigExcelToolbar;
